import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { hueFor, initials } from '../lib/format.ts';
import { Icon, type IconName } from './Icon.tsx';
import { EASE } from './motion.tsx';

// ---------------------------------------------------------------- Button
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  iconOnly?: boolean;
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'default', size = 'md', icon, iconRight, loading, iconOnly, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      className={clsx('btn', variant !== 'default' && `btn--${variant}`, size !== 'md' && `btn--${size}`, iconOnly && 'btn--icon', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden /> : icon ? <Icon name={icon} /> : null}
      {children}
      {iconRight && !loading ? <Icon name={iconRight} /> : null}
    </button>
  );
});

// ---------------------------------------------------------------- Field
export function Field({ label, hint, error, children, aside }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode; aside?: ReactNode }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        <span>{label}</span>
        {aside}
      </label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': hint || error ? hintId : undefined })}
      {error ? (
        <span id={hintId} className="field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** `showLabel` puts the label beside the switch (clicking it toggles); otherwise it is only announced. */
export function Switch({ checked, onChange, label, disabled, showLabel }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean; showLabel?: boolean }) {
  const control = <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" disabled={disabled} onClick={() => onChange(!checked)} />;
  return showLabel ? (
    <label className="switch-field" data-disabled={disabled || undefined}>
      {control}
      <span>{label}</span>
    </label>
  ) : (
    control
  );
}

// ---------------------------------------------------------------- Chips
/** ok = green (active, present, done), wait and warn = amber (pending, on leave, late), bad = red (failed, absent, rejected), info = blue. */
export type Tone = 'ok' | 'wait' | 'warn' | 'bad' | 'info' | 'accent' | 'plain';
export function Chip({ tone = 'plain', children, live }: { tone?: Tone; children: ReactNode; live?: boolean }) {
  return <span className={clsx('chip', tone !== 'plain' && `chip--${tone}`, live && 'chip--live')}>{children}</span>;
}

const STATUS: Record<string, [Tone, string]> = {
  pending: ['wait', 'Pending'],
  approved: ['ok', 'Approved'],
  rejected: ['bad', 'Rejected'],
  cancelled: ['plain', 'Cancelled'],
  expired: ['plain', 'Expired'],
  draft: ['plain', 'Draft'],
  calculating: ['info', 'Calculating'],
  review: ['wait', 'In review'],
  paid: ['ok', 'Paid'],
  active: ['ok', 'Active'],
  onboarding: ['info', 'Onboarding'],
  resigned: ['plain', 'Resigned'],
  terminated: ['bad', 'Terminated'],
  valid: ['ok', 'On time'],
  present: ['ok', 'Present'],
  attention: ['warn', 'Attention'],
  late: ['warn', 'Late'],
  missing: ['bad', 'Missing'],
  absent: ['bad', 'Absent'],
  unscheduled: ['info', 'Unscheduled'],
  in: ['ok', 'In'],
  done: ['ok', 'Done'],
  not_in: ['bad', 'Not in'],
  upcoming: ['info', 'Later'],
  day_off: ['plain', 'Day off'],
  leave: ['wait', 'On leave'],
  public_holiday: ['info', 'Holiday'],
  queued: ['wait', 'Queued'],
  sent: ['wait', 'Waiting'],
  failed: ['bad', 'Failed'],
};
export function StatusChip({ status, live }: { status: string; live?: boolean }) {
  const [tone, label] = STATUS[status] ?? ['plain', status.replace(/_/g, ' ')];
  return (
    <Chip tone={tone} live={live}>
      {label}
    </Chip>
  );
}

// ---------------------------------------------------------------- Avatar
/** Initials on a colour from the name; with `photo` (see `photoUrl`), the person's picture, falling back to the initials. */
export function Avatar({ name, size = 36, status, photo }: { name: string; size?: number; status?: 'in' | 'out' | 'late' | null; photo?: string | null }) {
  const color = status === 'in' ? 'var(--ok)' : status === 'late' ? 'var(--wait)' : status === 'out' ? 'var(--ink-4)' : undefined;
  // Remembered per address, so a new photo gets its own try.
  const [failed, setFailed] = useState<string | null>(null);
  const showPhoto = !!photo && failed !== photo;
  return (
    <span className={clsx('avatar', showPhoto && 'avatar--photo')} style={{ ['--size' as string]: `${size}px`, ['--h' as string]: hueFor(name) }} aria-hidden="true">
      {showPhoto ? <img className="avatar__img" src={photo} alt="" loading="lazy" decoding="async" onError={() => setFailed(photo)} /> : initials(name)}
      {color ? <span className="avatar__dot" style={{ background: color }} /> : null}
    </span>
  );
}

// ---------------------------------------------------------------- Panel
export function Panel({ title, eyebrow, action, children, className, bodyClassName, flush }: { title?: ReactNode; eyebrow?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string; flush?: boolean }) {
  return (
    <section className={clsx('panel', flush && 'panel--flush', className)}>
      {title || eyebrow || action ? (
        <header className="panel__head">
          <div className="stack" style={{ ['--gap' as string]: '6px' }}>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            {title ? <h2 className="panel__title">{title}</h2> : null}
          </div>
          {action}
        </header>
      ) : null}
      <div className={clsx('panel__body', bodyClassName)}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------- Tabs
export function Tabs<T extends string>({ tabs, value, onChange, idPrefix = 'tabs' }: { tabs: { id: T; label: ReactNode; count?: number }[]; value: T; onChange: (v: T) => void; idPrefix?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const onKey = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.id === value);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!;
      onChange(next.id);
      ref.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.id}"]`)?.focus();
    }
  };
  return (
    <div className="tabs" role="tablist" ref={ref} onKeyDown={onKey}>
      {tabs.map((t) => (
        <button key={t.id} data-tab={t.id} role="tab" type="button" className="tabs__tab" aria-selected={t.id === value} tabIndex={t.id === value ? 0 : -1} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined ? <span className="tabs__count">{t.count}</span> : null}
          {t.id === value ? <motion.span layoutId={`${idPrefix}-ink`} className="tabs__ink" transition={{ type: 'spring', stiffness: 520, damping: 40 }} /> : null}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label, idPrefix = 'seg' }: { options: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; label: string; idPrefix?: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={o.id === value} onClick={() => onChange(o.id)}>
          {o.id === value ? <motion.span layoutId={`${idPrefix}-thumb`} className="segmented__thumb" transition={{ type: 'spring', stiffness: 520, damping: 42 }} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Dialog
export function Dialog({ open, onClose, title, eyebrow, children, footer, width }: { open: boolean; onClose: () => void; title: ReactNode; eyebrow?: ReactNode; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => ref.current?.querySelector<HTMLElement>('input, textarea, select, button:not([data-close])')?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((x) => !x.hasAttribute('disabled'));
        if (!f.length) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus();
    };
  }, [open, onClose]);
  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div className="scrim" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} />
          <motion.div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="dialog"
            style={width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined}
            initial={{ opacity: 0, x: '-50%', y: 'calc(-50% + 18px)', scale: 0.98 }}
            animate={{ opacity: 1, x: '-50%', y: '-50%', scale: 1 }}
            exit={{ opacity: 0, x: '-50%', y: 'calc(-50% + 10px)', scale: 0.985 }}
            transition={{ duration: 0.38, ease: EASE }}
          >
            <header className="dialog__head">
              <div className="row row--between" style={{ alignItems: 'start' }}>
                <div className="stack" style={{ ['--gap' as string]: '8px' }}>
                  {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
                  <h2 id={titleId} className="dialog__title">
                    {title}
                  </h2>
                </div>
                <Button variant="ghost" iconOnly icon="x" aria-label="Close" onClick={onClose} data-close />
              </div>
            </header>
            <div className="dialog__body">{children}</div>
            {footer ? <footer className="dialog__foot">{footer}</footer> : null}
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

// ---------------------------------------------------------------- Toasts
type Toast = { id: number; title: string; body?: string; tone?: 'ok' | 'error' };
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.tone === 'error' ? 7000 : 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        <AnimatePresence initial={false}>
          {items.map((t) => (
            <motion.div key={t.id} layout className={clsx('toast', t.tone === 'error' && 'toast--error')} initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, x: 40 }} transition={{ duration: 0.4, ease: EASE }}>
              <span className="toast__mark" />
              <div>
                <div className="toast__title">{t.title}</div>
                {t.body ? <div className="muted">{t.body}</div> : null}
              </div>
              <button className="btn btn--ghost btn--sm btn--icon" aria-label="Dismiss" onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
                <Icon name="x" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ---------------------------------------------------------------- Feedback
export function Skeleton({ w = '100%', h = 14, r }: { w?: number | string; h?: number; r?: number }) {
  return <span className="skeleton" style={{ display: 'block', width: w, height: h, borderRadius: r }} aria-hidden />;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <svg width="64" height="40" viewBox="0 0 64 40" fill="none" aria-hidden>
        <path d="M4 36h56" stroke="var(--line-3)" />
        <path d="M14 36V14h14v22M36 36V6h14v30" stroke="var(--ink-4)" />
        <path d="M18 36h6" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <div className="empty__title">{title}</div>
      {children ? <p style={{ maxWidth: '42ch' }}>{children}</p> : null}
      {action}
    </div>
  );
}

export function Callout({ tone = 'warn', title, children }: { tone?: 'warn' | 'bad' | 'info'; title?: ReactNode; children: ReactNode }) {
  return (
    <div className={clsx('callout', tone === 'bad' && 'callout--bad', tone === 'info' && 'callout--info')} role={tone === 'bad' ? 'alert' : undefined}>
      <Icon name={tone === 'info' ? 'info' : 'alert'} />
      <div>
        {title ? <strong>{title} </strong> : null}
        {children}
      </div>
    </div>
  );
}

export function PageHead({ eyebrow, title, lede, aside, compact }: { eyebrow?: ReactNode; title: ReactNode; lede?: ReactNode; aside?: ReactNode; compact?: boolean }) {
  return (
    <motion.header className={clsx('page-head', compact && 'page-head--compact')} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: EASE }}>
      <div className="page-head__text">
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1 className="page-head__title">{title}</h1>
        {lede ? <p className="page-head__lede">{lede}</p> : null}
      </div>
      {aside ? <div className="page-head__aside">{aside}</div> : null}
    </motion.header>
  );
}

export function useConfirm() {
  const [state, setState] = useState<{ title: string; body: ReactNode; confirm: string; tone?: 'danger'; resolve: (v: boolean) => void } | null>(null);
  const ask = (title: string, body: ReactNode, confirm = 'Confirm', tone?: 'danger') => new Promise<boolean>((resolve) => setState({ title, body, confirm, tone, resolve }));
  const node = (
    <Dialog
      open={!!state}
      onClose={() => {
        state?.resolve(false);
        setState(null);
      }}
      title={state?.title ?? ''}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              state?.resolve(false);
              setState(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={state?.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => {
              state?.resolve(true);
              setState(null);
            }}
          >
            {state?.confirm}
          </Button>
        </>
      }
    >
      <div className="muted">{state?.body}</div>
    </Dialog>
  );
  return { ask, node };
}
